import {ResponseCodes} from './response-codes';
import {HttpClient} from 'aurelia-fetch-client';
import {Message} from '../wc-ui/index';
import assign from 'object.assign';
assign.shim();
import 'isomorphic-fetch';
import {ResponseError} from './response-errors';
import {parseJsonToObject} from '../wc-converters/json';

// Polyfill
if (typeof Object.assign != 'function') {
  // Must be writable: true, enumerable: false, configurable: true
  Object.defineProperty(Object, "assign", {
    value: function assign(target: any) { // .length of function is 2
      'use strict';
      if (target == null) { // TypeError if undefined or null
        throw new TypeError('Cannot convert undefined or null to object');
      }

      var to = Object(target);

      for (var index = 1; index < arguments.length; index++) {
        var nextSource = arguments[index];

        if (nextSource != null) { // Skip over if undefined or null
          for (var nextKey in nextSource) {
            // Avoid bugs when hasOwnProperty is shadowed
            if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
              to[nextKey] = nextSource[nextKey];
            }
          }
        }
      }
      return to;
    },
    writable: true,
    configurable: true
  });
}

export class HttpClientManager {
  /**
   * Function to create a wrapper for the aurelia http client without interceptors
   */
  static httpClient(): HttpClient {
    let http = new HttpClient();
    http.configure(config => {
      config.withDefaults({
        credentials: 'same-origin' // Valid values; omit, same-origin and include
      });
    });
    return http;
  }

  /**
   * Function to create a wrapper for the aurelia http client with Interceptors
   */
  static httpClientWithInterceptors(unauthenticatedRedirect: boolean = true, enableErrorMessages: boolean = true, unauthenticatedRedirectUrl: string = '/#/signin'): HttpClient {
    let http = new HttpClient();
    http.configure((httpConfig: any) => {
      httpConfig
        .withDefaults({
          headers: {
            'Accept': 'application/json'
          },
          credentials: 'same-origin'
        })
        .withInterceptor({
          request(request: any) {
            return request;
          },
          requestError(request: any) {
            console.log(`Request error ${request}`);
            return request;
          },
          response(response: any) {
            switch(response.status) {
              case ResponseCodes.success:
                return response;
              case ResponseCodes.unauthorised:
                if (unauthenticatedRedirect) {
                  // Redirect to portal sign-in
                  console.log(`Unauthorised - redirecting to sign-in`);
                  window.top.location.assign(unauthenticatedRedirectUrl);
                } else {
                  if (enableErrorMessages)
                    Message.error('Authentication failed, verify your account exists in URM');
                }
                break;
              default:
                if (enableErrorMessages)
                  Message.error(HttpClientManager.responseErrorMessage(response)); 
            }
            
            return response;
          },
          responseError(response: any) {
            if (enableErrorMessages)
              Message.error(HttpClientManager.responseErrorMessage(response)); 
            return response;
          }
        });
    });
    return http;
  }

  static responseErrorMessage(response): string {
    let errorMessage = response.statusText;

    switch(response.status) {
      case ResponseCodes.success:
        return response;
      case ResponseCodes.badRequest:          
        errorMessage = `Request failed. ${response.status} ${response.url}`;
        break;
      case ResponseCodes.unauthorised:
        errorMessage = 'Authentication failed, verify your account exists in URM';
        break;
      case ResponseCodes.forbidden:
        errorMessage = 'You do not have sufficient permissions';
        break;
      case ResponseCodes.notFound: 
        errorMessage = `Request failed, end point not found. ${response.status} ${response.url}`;
        break;
      case ResponseCodes.timeOut:
        errorMessage = 'Looks like the server is taking to long to respond, this can be caused by either poor connectivity or an error with our servers. Please try again in a while';
        break;
      case ResponseCodes.serverError:
        errorMessage = `Request failed, service error. Contact AEMO support hub ${response.status} ${response.url}`;
        break;
      default:
        errorMessage = 'Request failed. Contact AEMO support hub';
        break;    
    }
    return errorMessage;
  }

  /**
   * Parses response from Http Client into ApiResponse object
   * This is useful for handling error messages and parsing json objects with dates
   *
   * @param jsonString The API response.
   * @param properties Properties that should have JSON.parse(value) called on them. Mostly used to specify which 
   * properties should be parsed as dates.
   * @returns ApiResponse object
   */
  static async normaliseResponse(response: any, ...properties: string[]): Promise<ApiResponse> {
    // Unhandled HTTP request errors 
    if (!response.status) {
      return {
        success: false,
        errorMessage: response.statusText
      };
    }

    if (response.status === ResponseCodes.success) {
      let responseData = parseJsonToObject((await response.text()), ...properties);
      return {
        success: true,
        data: responseData ? responseData.data : {}
      };
    } else if (response.status === ResponseCodes.businessValidation || response.status === ResponseCodes.badRequest) {
      return {
        success: false,
        validationErrors: (await response.json()).errors
      };
    } else {
      return {
        success: false,
        errorMessage: this.responseErrorMessage(response)
      };
    }
  }
}

export interface ApiResponse {
  success: boolean;
  data?: any;
  errorMessage?: string;
  validationErrors?: Array<ResponseError>;
}



// WEBPACK FOOTER //
// node_modules/wc-lib/src/wc-http-client/wc-client.ts